<template>
  <v-container fluid class="pa-6 container-background">

    <!-- HEADER -->
    <v-card class="main-card pa-6 mb-6">
      <div class="d-flex align-center justify-space-between flex-wrap">
        <div>
          <h1 class="main-title">
            <v-icon color="rgb(204 33 40)" class="mr-2">
              mdi-form-select
            </v-icon>

            Configuración de campos
          </h1>

          <p class="main-subtitle">
            Personaliza los campos del formulario configurando su nombre, visibilidad y obligatoriedad.
Si no se define una configuración, el sistema conservará el nombre original y los campos serán opcionales.
          </p>
        </div>
      </div>
    </v-card>

    <!-- INFO -->
    <v-card v-if="items.length == 0" class="info-card pa-5 mb-6">
      <div class="d-flex align-start">
        <v-icon color="rgb(204 33 40)" class="mr-3 mt-1">
          mdi-information-outline
        </v-icon>

        <div>
          <div class="info-title">
            Configuración inteligente de campos
          </div>

          <div class="info-text">
            Realice la configuración inicial del formulario para poder utilizar las funcionalidades de este módulo.
          </div>
        </div>
      </div>
    </v-card>

    <!-- CAMPOS -->
    <v-row>
      <v-col
        v-for="item in items"
        :key="item.key"
        cols="12"
        md="6"
        lg="6"
      >
        <v-card class="field-card pa-5">

          <!-- HEADER CARD -->
          <div class="d-flex align-center justify-space-between mb-4">
            <div class="field-name">
              {{ item.description }}
            </div>

            <v-chip
              small
              :color="item.mandatory ? 'red lighten-5' : 'blue lighten-5'"
              :text-color="item.mandatory ? 'red darken-2' : 'blue darken-2'"
            >
              {{ item.mandatory ? 'Obligatorio' : 'Configurable' }}
            </v-chip>
          </div>

          <!-- ALIAS -->
          <label class="custom-label">
            Nombre a mostrar
          </label>

          <v-text-field
            v-model="item.alias"
            outlined
            dense
            placeholder="Ingrese un alias"
            class="custom-input"
          >
            <template v-slot:append>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    small
                    color="grey"
                    v-bind="attrs"
                    v-on="on"
                  >
                    mdi-information-outline
                  </v-icon>
                </template>

                <span>
                  Personaliza el nombre que verá el cliente.
                </span>
              </v-tooltip>
            </template>
          </v-text-field>

          <!-- REQUIRED -->
          <div v-if="!item.mandatory">
            <label class="custom-label">
              Nivel de exigencia
            </label>

            <v-select
              v-model="item.required"
              :items="requiredOptions"
              item-text="name"
              item-value="value"
              outlined
              dense
              prepend-inner-icon="mdi-file-check-outline"
              class="custom-input"
              :disabled="isRequiredDisabled(item)"
              @change="onRequiredChange(item, $event)"
            />
          </div>


          <!-- VISIBILIDAD -->
          <div v-if="!item.mandatory" class="mt-2">
            <label class="custom-label">
              Estado de visualización
            </label>

            <v-select
              v-model="item.visible"
              :items="visibleOptions"
              item-text="name"
              item-value="value"
              outlined
              dense
              prepend-inner-icon="mdi-eye-outline"
              class="custom-input"
              :disabled="isVisibleDisabled(item)"
              @change="onVisibleChange(item, $event)"
            />
          </div>

          <!-- ESTADO -->
          <div class="status-container mt-4">
            <div class="status-item">
              <v-icon
                small
                :color="item.visible ? 'green' : 'grey'"
              >
                mdi-circle
              </v-icon>

              <span>
                {{ item.visible ? 'Visible' : 'Oculto' }}
              </span>
            </div>

            <div class="status-item">
              <v-icon
                small
                :color="item.required ? 'orange' : 'grey'"
              >
                mdi-alert-circle
              </v-icon>

              <span>
                {{ item.required ? 'Requerido' : 'Opcional' }}
              </span>
            </div>
          </div>

        </v-card>
      </v-col>
    </v-row>

    <!-- ALERT -->
    <v-row justify="center">
      <v-col cols="12" sm="8" md="6">
        <v-alert
          v-if="showAlert"
          :type="alertType"
          border="left"
          colored-border
          elevation="2"
          class="my-4"
        >
          {{ alertMessage }}
        </v-alert>
      </v-col>
    </v-row>

    <!-- BUTTON -->
    <div class="d-flex justify-end mt-4">
      <v-btn
        color="rgb(204 33 40)"
        x-large
        elevation="2"
        rounded
        class="white--text px-10 font-weight-bold"
        @click="save"
      >
        <v-icon left>
          mdi-content-save-outline
        </v-icon>

        Guardar configuración
      </v-btn>
    </div>

    <!-- LOADING -->
    <v-overlay :value="loading" opacity="0.6" z-index="9999">
      <v-progress-circular
        indeterminate
        size="64"
        color="rgb(204 33 40)"
      />
    </v-overlay>

  </v-container>
</template>


<script>
import axios from "axios";
import { environment } from "@/data/environment";
export default {
  name: "UserRolesPermissions",
  data() {
    return {
      items: [],
      requiredOptions: [
        { value: true, name: "Requerido" },
        { value: false, name: "Opcional" },
      ],
      loading: false,
      showAlert: false,
      alertMessage: "",
      alertType: "success",
      visibleOptions: [
        { value: true, name: "Visible" },
        { value: false, name: "Oculto" },
      ],
      locationsAddress: ["address", "department", "city", "postalCode"],
      isUpdatingLocation: false, // Flag para evitar bucles infinitos
    };
  },
  watch: {
    items: {
      deep: true,
      handler(items) {
        // Solo aplicar reglas a campos NO de ubicación
        items.forEach((item) => {
          if (!this.isLocationField(item)) {
            if (item.required === true) {
              item.visible = true;
            }
            if (item.visible === false) {
              item.required = false;
            }
          }
        });
      },
    },
  },
  mounted() {
    this.fetchConfig();
  },
  methods: {
    async fetchConfig() {
      try {
        this.loading = true;
        let idCompany = JSON.parse(
          atob(localStorage.getItem("uid"))
        ).idcompania;
        const response = await axios.post(
          `${environment.HIOPOSWEBSERVICES}/api/habeas-data/get-config-form-by-company`,
          {
            idCompany: idCompany,
          }
        );
        this.items = response.data.data;
      } catch (error) {
        console.error("Error al obtener configuración:", error);
      } finally {
        this.loading = false;
      }
    },
    async save() {
      this.loading = true;

      try {
        let idCompany = JSON.parse(
          atob(localStorage.getItem("uid"))
        ).idcompania;
        console.log(this.items);

        const response = await axios.post(
          `${environment.HIOPOSWEBSERVICES}/api/habeas-data/save-config-form-input`,
          {
            idCompany: idCompany,
            data: this.items,
          }
        );
        if (response.data.error) {
          this.setAlert("error", response.data.message);
        } else {
          this.setAlert("success", response.data.message);
          this.items = response.data.data;
        }
      } catch (error) {
        console.error("Error guardando configuración:", error);
        this.setAlert("error", "Error al guardar la configuración.");
      } finally {
        this.loading = false;
      }
    },
    setAlert(type, message) {
      this.alertType = type;
      this.alertMessage = message;
      this.showAlert = true;
      setTimeout(() => (this.showAlert = false), 4000);
    },
    isRequiredDisabled(item) {
      return item.visible === false;
    },
    isVisibleDisabled(item) {
      return item.required === true;
    },
    isLocationField(item) {
      return this.locationsAddress.includes(item.name);
    },
    // Sincronizar campos de ubicación cuando cambia "required"
    onRequiredChange(item, newValue) {
      if (this.isLocationField(item) && !this.isUpdatingLocation) {
        this.isUpdatingLocation = true;
        this.syncLocationFields("required", newValue);
        this.isUpdatingLocation = false;
      }
    },
    // Sincronizar campos de ubicación cuando cambia "visible"
    onVisibleChange(item, newValue) {
      if (this.isLocationField(item) && !this.isUpdatingLocation) {
        this.isUpdatingLocation = true;
        this.syncLocationFields("visible", newValue);
        this.isUpdatingLocation = false;
      }
    },
    // Método para sincronizar los tres campos de ubicación
    syncLocationFields(property, value) {
      this.items.forEach((item) => {
        if (this.isLocationField(item)) {
          item[property] = value;

          // Aplicar las reglas de dependencia
          if (property === "required" && value === true) {
            item.visible = true;
          }
          if (property === "visible" && value === false) {
            item.required = false;
          }
        }
      });
    },
  },
};
</script>


<style scoped>
.container-background {
  /* background: #f8fafc; */
  min-height: 100vh;
}

.main-card {
  border-radius: 18px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 14px rgba(0,0,0,.04);
}

.main-title {
  font-size: 30px;
  font-weight: 700;
  /* color: #1e293b; */
  display: flex;
  align-items: center;
}

.main-subtitle {
  /* color: #6b7280; */
  margin-top: 4px;
  font-size: 14px;
}

.info-card {
  border-radius: 16px;
  border: 1px solid #fde68a;
  background: #fffdf5;
}

.info-title {
  font-weight: 700;
  color: #92400e;
  margin-bottom: 4px;
}

.info-text {
  color: #78350f;
  font-size: 14px;
  line-height: 1.6;
}

.field-card {
  border-radius: 18px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 14px rgba(0,0,0,.04);
  transition: .2s ease;
  height: 100%;
}

.field-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0,0,0,.08);
}

.field-name {
  font-size: 18px;
  font-weight: 700;
  /* color: #1e293b; */
  line-height: 1.4;
}

.custom-label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 600;
  /* color: #374151; */
}

.status-container {
  display: flex;
  gap: 16px;
  padding-top: 14px;
  border-top: 1px solid #e5e7eb;
}

.status-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #6b7280;
}

::v-deep .v-text-field--outlined fieldset,
::v-deep .v-select--outlined fieldset {
  border-color: #d1d5db !important;
  border-radius: 12px;
}

::v-deep .v-text-field--outlined.v-input--is-focused fieldset,
::v-deep .v-select--outlined.v-input--is-focused fieldset {
  border-color: rgb(204 33 40) !important;
  border-width: 2px;
}

::v-deep .v-input input {
  font-size: 14px;
}

::v-deep .v-btn {
  text-transform: none;
  letter-spacing: .3px;
}
</style>